import React, {useEffect, useState} from 'react';

import styles from './UploadAssetModal.module.less';
import {message, Modal, Upload, UploadFile, UploadProps} from 'antd';
import {InboxOutlined} from '@ant-design/icons';
import {API_BASE_URL} from '../../../config';
import {useAppDispatch, useAppSelector} from '../../../app/hooks';
import {selectToken} from '../../auth/authSlice';
import {fetchAssetsByProjectId, fetchGeomsBySpaceId, setAssets} from '../tuzhiVisSlice';
import TuzhiVisService from '../TuzhiVis.service';
import {RcFile, UploadChangeParam} from 'antd/es/upload';

/**
 * 三步上传模式中，文件接口的信息
 */
interface UploadEndpointInfo {
  uploadUrl: string;
  confirmKey: string;
  expireAt: Date;
}

interface ModalProps {
  projectId: number;
  open: boolean;
  setOpen: (open: boolean) => void;
}

function UploadAssetModal(props: ModalProps) {
  const token = useAppSelector(selectToken);
  const {projectId, open, setOpen} = props;

  const [uploadEndpointInfo, setUploadEndpointInfo] = useState<UploadEndpointInfo | null>(null);
  const [isUploadDisabled, setIsUploadDisabled] = useState(false);

  const dispatch = useAppDispatch();

  const initUpload = async () => {
    const res = await TuzhiVisService
      .initiateUpload(projectId);
    setUploadEndpointInfo(res.data);
  };

  useEffect(() => {
    if (open) {
      try {
        initUpload();
      } catch (e) {
        console.log(e);
        if (e instanceof Error) {
          message.error(e.message);
        }
      }
    }
  }, [open]);

  const handleClose = () => {
    if (!isUploadDisabled) {
      setOpen(false);
    }
  };

  const beforeUpload = async (file: RcFile) => {
    if (!uploadEndpointInfo) {
      return false;
    }

    const expire = Number(uploadEndpointInfo.expireAt) * 1000;

    if (expire < Date.now()) {
      await initUpload();
    }
    // 修改成真正上传的 URL
    // file.action = uploadEndpointInfo.uploadUrl;
    return file;
  };

  const handleUpload: UploadProps['customRequest'] = async (options) => {
    const {file, onSuccess, onError, onProgress} = options;

    if (!uploadEndpointInfo)
      return;

    try {
      // 自定义的上传逻辑，直接上传生数据，不使用 multipart
      // 由于原生 fetch 不支持进度展示，故暂时废弃
      // const res = await fetch(uploadEndpointInfo.uploadUrl, {
      //   method: 'PUT',
      //   body: file as RcFile,   // 直接传生数据
      //   headers: {
      //     'Content-Type': (file as RcFile).type || 'application/octet-stream',
      //   },
      // });
      // if (res.ok) {
      //   onSuccess?.(res);
      // } else {
      //   throw new Error(`上传失败：${res.status}`);
      // }

      // 用 xhr 支持进度回调
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadEndpointInfo.uploadUrl);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress({percent});
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onSuccess?.(xhr.response, xhr);
        } else {
          onError?.(new Error(`上传失败：${xhr.statusText}`));
        }
      };

      xhr.onerror = () => {
        onError?.(new Error('上传失败'));
      };

      xhr.setRequestHeader('Content-Type', (file as File).type || 'application/octet-stream');
      xhr.send(file as Blob);


    } catch (err) {
      onError?.(err as Error);
    }
  };

  const onFileChange = async (info: UploadChangeParam<UploadFile<any>>) => {
    const {status} = info.file;
    if (status !== 'uploading') {
      console.log(info.file, info.fileList);
    } else {
      setIsUploadDisabled(true);
    }
    if (status === 'done') {
      if (!uploadEndpointInfo)
        return;

      setIsUploadDisabled(false);
      // 此时调用上传后处理接口，进行 zip 后处理流程如解压
      const confirmRes = await TuzhiVisService.confirmUpload(projectId, uploadEndpointInfo.confirmKey);
      console.log(confirmRes);
      message.success(`${info.file.name} 上传成功。`);
      // 上传后自动刷新资源树
      // todo 更改写法
      TuzhiVisService
        .getAssetsByProjectId(projectId)
        .then(res => {
          dispatch(setAssets(res.data));
        });
    } else if (status === 'error') {
      setIsUploadDisabled(false);
      message.error(`${info.file.name} 上传失败。`);
    }
  };

  const uploadProps: UploadProps = {
    name: 'zip',
    // 使用自定义上传逻辑，不再需要 action 配置
    // action: uploadEndpointInfo && uploadEndpointInfo.uploadUrl || '',
    // method: 'PUT',
    // 直接使用后缀而不是 mimetype，避免拖拽失败问题
    // accept: 'application/zip',
    accept: '.zip',
    multiple: true,
    disabled: isUploadDisabled,
    beforeUpload: beforeUpload,
    customRequest: handleUpload,
    onChange: onFileChange,
  };

  return (
    <div className={styles.root}>
      <Modal
        title="打包上传文件"
        open={open}
        onOk={handleClose}
        onCancel={handleClose}
        okText="关闭"
        okButtonProps={{disabled: isUploadDisabled}}
        cancelButtonProps={{style: {display: 'none'}}}
      >
        <p>zip包组织结构按 根目录/建筑名/专业名/图纸.pdf 层级组织。</p>
        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined/>
          </p>
          <p className="ant-upload-text">点击上传图纸压缩包</p>
          <p className="ant-upload-hint">
            请上传按规定目录结构打包的zip文件
          </p>
        </Upload.Dragger>
      </Modal>
    </div>
  );
}

export default UploadAssetModal;
